<template>
  <Teleport to="body">
    <div v-if="open && detail" class="sub-detail-overlay" @click.self="emitClose">
      <div class="sub-detail-shell" @click.stop>
        <div class="sub-detail-panel">
          <header class="sub-detail-head">
            <h2>HomeMetric Care Subscription Detail</h2>
            <button type="button" class="sub-detail-x" aria-label="Close" @click="emitClose">
              <img :src="imgClose" class="sub-detail-x-ic" alt="" />
            </button>
          </header>

          <button
            type="button"
            class="cancel-btn"
            :class="{ disabled: !canCancel }"
            :disabled="!canCancel"
            @click="onCancel"
          >
            <img :src="canCancel ? cancelIcon : cancelIconDisabled" alt="" />
            <span class="cancel-btn-txt">Cancel</span>
          </button>

          <div class="sub-detail-body">
            <div class="sub-info" :style="{ '--sub-label-w': labelWidthPx }">
              <div class="sub-info-row sub-info-row--action">
                <span ref="subscriptionIdLabelRef" class="sub-info-label">Subscription ID:</span>
                <span class="sub-info-value">{{ detail.subscriptionId }}</span>
                <button type="button" class="view-linked-orders" @click="onViewLinkedOrders">
                  View linked orders
                </button>
              </div>

              <div class="sub-info-row">
                <span class="sub-info-label">User ID:</span>
                <span class="sub-info-value">{{ detail.userId }}</span>
              </div>

              <div class="sub-info-row">
                <span class="sub-info-label">Home ID:</span>
                <span class="sub-info-value">{{ detail.homeId }}</span>
              </div>

              <div class="sub-info-row">
                <span class="sub-info-label">Registered Mobile:</span>
                <span class="sub-info-value">{{ detail.registeredMobile }}</span>
              </div>

              <div class="sub-info-row">
                <span class="sub-info-label">Plan:</span>
                <span class="sub-info-value">{{ detail.plan }}</span>
              </div>

              <div class="sub-info-row">
                <span class="sub-info-label">Status:</span>
                <span
                  class="sub-info-value status-text"
                  :class="{
                    inactive:
                      detail.status === 'Inactive' ||
                      detail.status === 'Inactive (Payment Failed)',
                  }"
                >
                  {{ detail.status }}
                </span>
              </div>

              <div class="sub-info-row">
                <span class="sub-info-label">Effective Time:</span>
                <span class="sub-info-value">{{ detail.effectiveTime }}</span>
              </div>

              <template v-if="showCancelEndFields">
                <div class="sub-info-row">
                  <span class="sub-info-label">Cancellation Time:</span>
                  <span class="sub-info-value">{{ detail.cancellationTime }}</span>
                </div>

                <div class="sub-info-row sub-info-row--top">
                  <span class="sub-info-label">End time:</span>
                  <span class="sub-info-value end-time-value">
                    <span>{{ detail.endTimeLine1 }}</span>
                    <span>{{ detail.endTimeLine2 }}</span>
                  </span>
                </div>
              </template>

              <div class="sub-info-row">
                <span class="sub-info-label">Service Address:</span>
                <span class="sub-info-value">{{ detail.serviceAddress }}</span>
              </div>

              <div class="sub-info-divider" aria-hidden="true" />

              <div class="sub-info-row">
                <span class="sub-info-label">Contact Person:</span>
                <span class="sub-info-value">{{ detail.contactPerson }}</span>
              </div>

              <div class="sub-info-row">
                <span class="sub-info-label">Phone Number:</span>
                <span class="sub-info-value">{{ detail.phoneNumber }}</span>
              </div>

              <div class="sub-info-divider" aria-hidden="true" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import imgClose from '@/assets/Fill 1@2x.png'
import cancelIcon from '@/assets/cancel subscription@2x.png'
import cancelIconDisabled from '@/assets/cancel subscription.white@2x.png'

export type SubscriptionDetailStatus =
  | 'Active'
  | 'Active (Ending Soon)'
  | 'Inactive'
  | 'Inactive (Payment Failed)'

export interface SubscriptionDetailData {
  /** 主键，取消 / 关联订单用 */
  id: number
  subscriptionId: string
  userId: string
  homeId: string
  registeredMobile: string
  plan: string
  status: SubscriptionDetailStatus
  canCancel: boolean
  effectiveTime: string
  serviceAddress: string
  contactPerson: string
  phoneNumber: string
  cancellationTime?: string
  endTimeLine1?: string
  endTimeLine2?: string
}

const props = defineProps<{
  open: boolean
  detail: SubscriptionDetailData | null
}>()

const emit = defineEmits<{
  close: []
  cancel: [detail: SubscriptionDetailData]
  'view-linked-orders': [detail: SubscriptionDetailData]
}>()

const subscriptionIdLabelRef = ref<HTMLElement | null>(null)
const labelWidthPx = ref('118px')

const isInactive = computed(
  () =>
    props.detail?.status === 'Inactive' ||
    props.detail?.status === 'Inactive (Payment Failed)',
)

const canCancel = computed(() => {
  if (!props.detail) return false
  if (props.detail.canCancel === false) return false
  if (isInactive.value) return false
  return true
})

const showCancelEndFields = computed(
  () =>
    props.detail?.status === 'Active (Ending Soon)' ||
    props.detail?.status === 'Inactive' ||
    props.detail?.status === 'Inactive (Payment Failed)',
)

function measureLabelWidth(): void {
  const el = subscriptionIdLabelRef.value
  if (!el) return
  const prevFlex = el.style.flex
  const prevWidth = el.style.width
  el.style.flex = '0 0 auto'
  el.style.width = 'auto'
  const w = Math.ceil(el.getBoundingClientRect().width)
  el.style.flex = prevFlex
  el.style.width = prevWidth
  if (w > 0) labelWidthPx.value = `${w}px`
}

watch(
  () => [props.open, props.detail] as const,
  async ([open]) => {
    if (!open) return
    await nextTick()
    measureLabelWidth()
  },
)

function emitClose(): void {
  emit('close')
}

function onCancel(): void {
  if (!props.detail || !canCancel.value) return
  emit('cancel', props.detail)
}

function onViewLinkedOrders(): void {
  if (!props.detail) return
  emit('view-linked-orders', props.detail)
}
</script>

<style scoped>
.sub-detail-overlay {
  position: fixed;
  inset: 0;
  z-index: 12400;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  box-sizing: border-box;
  padding: 24px 24px 40px;
}

.sub-detail-shell {
  width: min(540px, 100%);
  height: calc(100vh - 64px);
  max-height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.sub-detail-panel {
  position: relative;
  width: 100%;
  height: 100%;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
  min-height: 0;
}

.sub-detail-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;
  padding: 24px 24px 0;
}

.sub-detail-head h2 {
  margin: 0;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #000000;
  text-align: left;
  flex: 1;
}

.sub-detail-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  flex-shrink: 0;
  padding: 0;
}

.sub-detail-x-ic {
  width: 14px;
  height: 14px;
  display: block;
}

.sub-detail-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 24px 24px 34px;
  box-sizing: border-box;
  scrollbar-width: none;
}

.sub-detail-body::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.sub-info {
  --sub-label-w: 118px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sub-info-row {
  display: flex;
  align-items: center;
  gap: 24px;
  min-width: 0;
}

.sub-info-row--action {
  flex-wrap: nowrap;
}

.sub-info-row--top {
  align-items: flex-start;
}

.sub-info-label {
  flex: 0 0 var(--sub-label-w);
  width: var(--sub-label-w);
  box-sizing: border-box;
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: right;
  font-style: normal;
  white-space: nowrap;
}

.sub-info-value {
  flex: 1 1 auto;
  min-width: 0;
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  word-break: break-word;
}

.end-time-value {
  display: flex;
  flex-direction: column;
}

.view-linked-orders {
  box-sizing: border-box;
  width: 119px;
  height: 20px;
  margin-left: auto;
  flex-shrink: 0;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  text-decoration-line: underline;
  white-space: nowrap;
}

.cancel-btn {
  position: absolute;
  top: 204px;
  right: 24px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-sizing: border-box;
  width: 83px;
  height: 28px;
  padding: 0;
  border-radius: 16px;
  border: 1px solid #0c3637;
  background: #ffffff;
  cursor: pointer;
  flex-shrink: 0;
}

.cancel-btn img {
  width: 16px;
  height: 16px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.cancel-btn-txt {
  box-sizing: border-box;
  width: 45px;
  height: 20px;
  font-family: SFPro, SFPro;
  font-weight: 400;
  font-size: 14px;
  color: #000000;
  line-height: 20px;
  text-align: left;
  font-style: normal;
  white-space: nowrap;
}

.cancel-btn.disabled,
.cancel-btn:disabled {
  border-color: #cccccc;
  cursor: default;
}

.cancel-btn.disabled .cancel-btn-txt,
.cancel-btn:disabled .cancel-btn-txt {
  color: #cccccc;
}

.status-text {
  color: #00aeef;
  font-weight: 600;
}

.status-text.inactive {
  color: #e54848;
}

.sub-info-divider {
  width: 100%;
  max-width: 492px;
  height: 1px;
  background: #e0e0e0;
  margin: 0;
  flex-shrink: 0;
}
</style>
